import { createAnnouncementCampaignConversation } from 'api/campaigns'
import { getStoreState } from 'config/store'
import { storeAnnouncementCampaignCompleted } from 'modules/campaigns'
import { createConversationEvent, triggerAPIEventForConversationAction } from 'modules/conversations'
import { getBotSenderId } from 'modules/embed/utils'
import { getEndUser, getSessionContext } from 'modules/session/selectors'
import { ActiveAnnouncementCampaign } from 'types/campaigns'
import { ConversationEventType } from 'types/conversation'
import { warn } from 'utils'

/**
 * Handles capturing leads from email capture announcement playbooks:
 * 1) Tell our public API event listeners that we submitted an email
 * 2) Mark the campaign as completed so it doesn't show again if set to only show once or once per session
 * 3) Create a conversation with the only messageType being EMAIL_CAPTURE so we can accurately report that both a conversation happened and an email was captured
 * @param email - email address captured
 * @param activeCampaign - an object containing announcement playbook information such as campaign id and playbook id
 */
export const captureLeadForEmailAnnouncementPlaybook = async (
  activeCampaign: ActiveAnnouncementCampaign,
  email: string,
) => {
  const state = getStoreState()

  try {
    const campaignId = activeCampaign.id
    storeAnnouncementCampaignCompleted(campaignId, activeCampaign.playbookId)

    createAnnouncementCampaignConversation({
      botId: getBotSenderId(),
      campaignBody: activeCampaign.body,
      campaignId: campaignId,
      contentType: 'EMAIL_CAPTURE',
      context: getSessionContext(state),
      displayType: 'EMAIL_CAPTURE',
      endUserBody: email,
      endUserId: getEndUser(state).id,
      leadCaptured: true,
      messageType: 'EMAIL_CAPTURE',
    })

    triggerAPIEventForConversationAction(campaignId, ConversationEventType.SUBMITTED)
  } catch(err) {
    warn(['Failed to capture lead for email announcement playbook', err])
  }
}

/**
 * Handler for when an announcement playbook is dismissed
 */
export const markAnnouncementPlaybookDismissed = async () => {
  try {
    return await createConversationEvent(ConversationEventType.DISMISSED)
  } catch(err) {
    warn(['Failed to capture dismissal event for announcement playbook', err])
  }
}

/**
 * Handler for when an announcement playbook is opened
 */
 export const markAnnouncementPlaybookOpened = async () => {
  try {
    return await createConversationEvent(ConversationEventType.OPENED)
  } catch(err) {
    warn(['Failed to capture opened event for announcement playbook', err])
  }
}

/**
 * Handler for when an announcement playbook is clicked
 */
export const markAnnouncementPlaybookClicked = async () => {
  try {
    return await createConversationEvent(ConversationEventType.CLICKED)
  } catch(err) {
    warn(['Failed to capture click event for announcement playbook', err])
  }
}
